/**
 * ===================================================
 * Module: frontend.src.components.PDFViewer.tsx
 * Description: PDF Viewer component - continuous vertical scroll through all pages
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * This script is proprietary and confidential.
 * Unauthorized copying, distribution, or modification of this
 * file, via any medium, is strictly prohibited.
 * ===================================================
 */

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RedactionBox, PDFViewerProps, PageMetadata } from '../types';

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfDoc,
  totalPages,
  scale,
  redactionBoxes,
  pageMetadata,
  isDrawing,
  isDrawMode,
  selectedBoxIds,
  focusedBoxId,
  scrollToPage,
  scrollToBox,
  onPageRender,
  onCurrentPageChange,
  onBoxSelect,
  onBoxCreate,
  onBoxUpdate,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onError
}) => {
  const { t } = useTranslation('pdfEditor');

  // Per-page refs (Maps keyed by page number)
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const overlayRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const containerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const pageWrapperRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const viewportsRef = useRef<Map<number, any>>(new Map());
  const pagesRef = useRef<Map<number, any>>(new Map());

  // Rendering state
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const renderingPagesRef = useRef<Set<number>>(new Set());
  const renderTasksRef = useRef<Map<number, any>>(new Map());

  // Pre-computed page dimensions (at current scale)
  const [pageDimensions, setPageDimensions] = useState<Map<number, { width: number; height: number }>>(new Map());

  // Drawing state
  const isDrawingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const drawingBoxRef = useRef<HTMLDivElement | null>(null);
  const drawingPageRef = useRef<number>(1);

  // Selection and drag state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragBoxId, setDragBoxId] = useState<string | null>(null);

  // Current page tracking (from scroll position)
  const scrollTrackingRef = useRef(false);
  const lastReportedPageRef = useRef(1);

  // Zoom anchoring (issue #15) — keeps the visible spot stable across scale changes
  const zoomAnchorRef = useRef<{ page: number; frac: number; hRatio: number } | null>(null);
  const prevScaleRef = useRef<number | null>(null);

  // Scroll-to-box centering (issue #21)
  const latestScrollToBoxRef = useRef<{ boxId: string; token: number } | null>(null);
  const completedBoxScrollTokenRef = useRef<number | null>(null);
  latestScrollToBoxRef.current = scrollToBox ?? null;

  // Render cache
  const RENDER_CACHE_SIZE = 30;
  const renderCacheRef = useRef<Map<string, {
    imageData: ImageData,
    viewport: any,
    page: any
  }>>(new Map());

  // ─────────────────────────────────────────────
  // Coordinate transforms (per-page)
  // ─────────────────────────────────────────────

  const pdfCoordsToPercentage = (pdfBbox: number[], pageNum: number) => {
    const viewport = viewportsRef.current.get(pageNum);
    const page = pagesRef.current.get(pageNum);
    if (!viewport) return null;

    try {
      const displayScale = viewport.scale;
      const rotation = page?.rotate ?? 0;

      let [x0, y0, x1, y1] = pdfBbox;
      const pageWidthPts = viewport.width / displayScale;
      const pageHeightPts = viewport.height / displayScale;

      if (rotation === 90) {
        const newX0 = pageWidthPts - y1, newY0 = x0, newX1 = pageWidthPts - y0, newY1 = x1;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 180) {
        const newX0 = pageWidthPts - x1, newY0 = pageHeightPts - y1, newX1 = pageWidthPts - x0, newY1 = pageHeightPts - y0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 270) {
        const newX0 = y0, newY0 = pageHeightPts - x1, newX1 = y1, newY1 = pageHeightPts - x0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      }

      const pctLeft = (Math.min(x0, x1) / pageWidthPts) * 100;
      const pctTop = (Math.min(y0, y1) / pageHeightPts) * 100;
      const pctWidth = (Math.abs(x1 - x0) / pageWidthPts) * 100;
      const pctHeight = (Math.abs(y1 - y0) / pageHeightPts) * 100;

      return { pctLeft, pctTop, pctWidth, pctHeight };
    } catch {
      return null;
    }
  };

  const canvasCoordsToPdfCoords = (canvasBbox: number[], pageNum: number): [number, number, number, number] => {
    const viewport = viewportsRef.current.get(pageNum);
    const page = pagesRef.current.get(pageNum);
    if (!viewport) return [canvasBbox[0], canvasBbox[1], canvasBbox[2], canvasBbox[3]];

    try {
      const displayScale = viewport.scale;
      const rotation = page?.rotate ?? 0;
      const [cx0, cy0, cx1, cy1] = canvasBbox;

      let x0 = cx0 / displayScale, y0 = cy0 / displayScale;
      let x1 = cx1 / displayScale, y1 = cy1 / displayScale;

      const pageWidthPts = viewport.width / displayScale;
      const pageHeightPts = viewport.height / displayScale;

      if (rotation === 90) {
        const px0 = y0, py0 = pageWidthPts - x1, px1 = y1, py1 = pageWidthPts - x0;
        x0 = px0; y0 = py0; x1 = px1; y1 = py1;
      } else if (rotation === 180) {
        const px0 = pageWidthPts - x1, py0 = pageHeightPts - y1, px1 = pageWidthPts - x0, py1 = pageHeightPts - y0;
        x0 = px0; y0 = py0; x1 = px1; y1 = py1;
      } else if (rotation === 270) {
        const px0 = pageHeightPts - y1, py0 = x0, px1 = pageHeightPts - y0, py1 = x1;
        x0 = px0; y0 = py0; x1 = px1; y1 = py1;
      }

      return [Math.min(x0, x1), Math.min(y0, y1), Math.max(x0, x1), Math.max(y0, y1)];
    } catch (error) {
      onError?.(`Failed to convert coordinates: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [canvasBbox[0], canvasBbox[1], canvasBbox[2], canvasBbox[3]];
    }
  };

  // ─────────────────────────────────────────────
  // Get mouse position relative to a page overlay
  // ─────────────────────────────────────────────

  const getRelativeMousePos = useCallback((e: React.MouseEvent | MouseEvent, pageNum: number): { x: number; y: number } => {
    const el = containerRefs.current.get(pageNum) ?? overlayRefs.current.get(pageNum);
    if (!el) return { x: 0, y: 0 };

    const rect = el.getBoundingClientRect();
    const viewport = viewportsRef.current.get(pageNum);
    if (!viewport) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    const scaleX = viewport.width / rect.width;
    const scaleY = viewport.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // Determine which page an event occurred on
  const getPageFromEvent = useCallback((e: React.MouseEvent | MouseEvent): number | null => {
    const target = e.target as HTMLElement;
    const pageWrapper = target.closest('[data-page-num]') as HTMLElement;
    if (!pageWrapper) return null;
    return parseInt(pageWrapper.dataset.pageNum || '0', 10);
  }, []);

  // ─────────────────────────────────────────────
  // Pre-compute page dimensions
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!pdfDoc || totalPages === 0) return;

    const computeDimensions = async () => {
      const dims = new Map<number, { width: number; height: number }>();
      for (let i = 1; i <= totalPages; i++) {
        try {
          const page = await pdfDoc.getPage(i);
          const vp = page.getViewport({ scale });
          dims.set(i, { width: vp.width, height: vp.height });
        } catch {
          dims.set(i, { width: 600 * scale, height: 800 * scale });
        }
      }
      setPageDimensions(dims);
    };

    computeDimensions();
  }, [pdfDoc, totalPages, scale]);

  // ─────────────────────────────────────────────
  // Render a single page to its canvas
  // ─────────────────────────────────────────────

  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDoc) return;
    if (renderingPagesRef.current.has(pageNum)) return;

    const canvas = canvasRefs.current.get(pageNum);
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const cacheKey = `${pageNum}-${scale}-${dpr}`;
    const cached = renderCacheRef.current.get(cacheKey);

    if (cached) {
      renderCacheRef.current.delete(cacheKey);
      renderCacheRef.current.set(cacheKey, cached);

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (context) {
        const viewport = cached.viewport;
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';
        canvas.width = cached.imageData.width;
        canvas.height = cached.imageData.height;
        context.putImageData(cached.imageData, 0, 0);

        viewportsRef.current.set(pageNum, viewport);
        pagesRef.current.set(pageNum, cached.page);

        syncOverlaySize(pageNum, viewport);
        renderBoxesForPage(pageNum);
        renderedPagesRef.current.add(pageNum);
        onPageRender(pageNum);
        return;
      }
    }

    try {
      renderingPagesRef.current.add(pageNum);

      const page = await pdfDoc.getPage(pageNum);
      const outputScale = dpr;
      const viewport = page.getViewport({ scale });
      const renderViewport = page.getViewport({ scale: scale * outputScale });

      if (!canvas) return;

      canvas.style.width = viewport.width + 'px';
      canvas.style.height = viewport.height + 'px';
      canvas.width = Math.ceil(renderViewport.width);
      canvas.height = Math.ceil(renderViewport.height);

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      viewportsRef.current.set(pageNum, viewport);
      pagesRef.current.set(pageNum, page);
      syncOverlaySize(pageNum, viewport);

      // Cancel any previous render task for this page
      const prevTask = renderTasksRef.current.get(pageNum);
      if (prevTask) {
        try { prevTask.cancel(); } catch { /* ignore */ }
      }

      const renderTask = page.render({ canvasContext: context, viewport: renderViewport });
      renderTasksRef.current.set(pageNum, renderTask);
      await renderTask.promise;
      renderTasksRef.current.delete(pageNum);

      // Cache the rendered image
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      renderCacheRef.current.set(cacheKey, { imageData, viewport, page });

      if (renderCacheRef.current.size > RENDER_CACHE_SIZE) {
        const firstKey = renderCacheRef.current.keys().next().value;
        if (firstKey !== undefined) renderCacheRef.current.delete(firstKey);
      }

      renderedPagesRef.current.add(pageNum);
      renderBoxesForPage(pageNum);
      onPageRender(pageNum);

    } catch (error: any) {
      if (error?.name === 'RenderingCancelledException') {
        // Will be re-rendered when visible again
      } else {
        onError?.(`Failed to render page ${pageNum}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      renderingPagesRef.current.delete(pageNum);
    }
  }, [pdfDoc, scale, onPageRender, onError]);

  // Sync overlay size for a specific page
  const syncOverlaySize = useCallback((pageNum: number, viewport: any) => {
    const w = viewport.width + 'px';
    const h = viewport.height + 'px';

    const overlay = overlayRefs.current.get(pageNum);
    if (overlay) { overlay.style.width = w; overlay.style.height = h; }

    const container = containerRefs.current.get(pageNum);
    if (container) { container.style.width = w; container.style.height = h; }
  }, []);

  // ─────────────────────────────────────────────
  // Render redaction boxes for a specific page
  // ─────────────────────────────────────────────

  const renderBoxesForPage = useCallback((pageNum: number) => {
    const overlay = overlayRefs.current.get(pageNum);
    if (!overlay) return;

    // Clear previously rendered boxes but preserve any in-progress drag
    // preview. Starting a drag calls setIsDrawing(true), which re-runs this
    // function; wiping the overlay destroyed the preview node, so the new box
    // only became visible once the drag finished and the box was committed.
    Array.from(overlay.children).forEach((child) => {
      if (!(child as HTMLElement).dataset.drawingPreview) child.remove();
    });

    Object.entries(redactionBoxes).forEach(([boxId, box]) => {
      if (box.page !== pageNum) return;

      const boxElement = document.createElement('div');
      boxElement.className = 'redaction-box';
      boxElement.dataset.boxId = boxId;

      const pct = pdfCoordsToPercentage(box.bbox, pageNum);
      if (pct) {
        boxElement.style.left = `${pct.pctLeft}%`;
        boxElement.style.top = `${pct.pctTop}%`;
        boxElement.style.width = `${pct.pctWidth}%`;
        boxElement.style.height = `${pct.pctHeight}%`;
      }

      const isSelected = selectedBoxIds.has(boxId);
      if (isSelected) {
        boxElement.classList.add('selected');
        // Handles go on the focused (last-clicked) box: with pinned checkbox
        // selections there is usually more than one selected box, so the old
        // size === 1 test would never be true.
        const handleTarget = focusedBoxId ?? (selectedBoxIds.size === 1 ? boxId : null);
        if (!isDrawMode && boxId === handleTarget) {
          boxElement.innerHTML = `
            <div class="resize-handle nw" data-handle="nw"></div>
            <div class="resize-handle ne" data-handle="ne"></div>
            <div class="resize-handle sw" data-handle="sw"></div>
            <div class="resize-handle se" data-handle="se"></div>
            <div class="resize-handle n" data-handle="n"></div>
            <div class="resize-handle s" data-handle="s"></div>
            <div class="resize-handle w" data-handle="w"></div>
            <div class="resize-handle e" data-handle="e"></div>
          `;
        }
      }

      if (isDrawing && isSelected) {
        boxElement.classList.add('drawing');
      }

      overlay.appendChild(boxElement);
    });
  }, [redactionBoxes, selectedBoxIds, focusedBoxId, isDrawMode, isDrawing]);

  // ─────────────────────────────────────────────
  // IntersectionObserver for lazy rendering
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!pdfDoc || pageDimensions.size === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const pageNum = parseInt((entry.target as HTMLElement).dataset.pageNum || '0', 10);
            if (pageNum > 0) {
              renderPage(pageNum);
            }
          }
        });
      },
      {
        root: document.querySelector('.pdf-viewer-container'),
        rootMargin: '200px 0px', // Pre-render 200px above/below viewport
        threshold: 0.01
      }
    );

    pageWrapperRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [pdfDoc, pageDimensions, renderPage]);

  // Re-render all visible pages when scale changes
  useEffect(() => {
    if (!pdfDoc || pageDimensions.size === 0) return;
    // Clear render state — pages will be re-rendered by IntersectionObserver
    renderedPagesRef.current.clear();
    renderCacheRef.current.clear();
    // Force re-render visible pages
    pageWrapperRefs.current.forEach((el, pageNum) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        const container = document.querySelector('.pdf-viewer-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const isVisible = rect.bottom > containerRect.top - 200 && rect.top < containerRect.bottom + 200;
          if (isVisible) {
            renderPage(pageNum);
          }
        }
      }
    });
  }, [scale, pdfDoc, pageDimensions]);

  // Re-render boxes when they change
  useEffect(() => {
    renderedPagesRef.current.forEach(pageNum => {
      renderBoxesForPage(pageNum);
    });
  }, [redactionBoxes, selectedBoxIds, focusedBoxId, isDrawMode, isDrawing, renderBoxesForPage]);

  // ─────────────────────────────────────────────
  // Scroll-to-page
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!scrollToPage || scrollToPage < 1 || scrollToPage > totalPages) return;

    const wrapper = pageWrapperRefs.current.get(scrollToPage);
    if (wrapper) {
      scrollTrackingRef.current = true; // Prevent feedback loop
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => { scrollTrackingRef.current = false; }, 500);
    }
  }, [scrollToPage, totalPages]);

  // ─────────────────────────────────────────────
  // Zoom anchoring (issue #15): capture the page/offset at the viewport top
  // when the scale changes, so the same spot can be restored after re-layout.
  // ─────────────────────────────────────────────

  useEffect(() => {
    const prev = prevScaleRef.current;
    prevScaleRef.current = scale;
    if (prev === null || prev === scale) return;

    // A pending scroll-to-box request takes priority over anchor restore
    const pendingBox = latestScrollToBoxRef.current;
    if (pendingBox && completedBoxScrollTokenRef.current !== pendingBox.token) return;

    const container = document.querySelector('.pdf-viewer-container') as HTMLElement | null;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const anchorY = containerRect.top + 1;

    let anchor: { page: number; frac: number } | null = null;
    pageWrapperRefs.current.forEach((el, pn) => {
      if (anchor || !el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top <= anchorY && rect.bottom > anchorY && rect.height > 0) {
        anchor = { page: pn, frac: (anchorY - rect.top) / rect.height };
      }
    });
    if (!anchor) return;

    const hRatio = container.scrollWidth > 0
      ? (container.scrollLeft + containerRect.width / 2) / container.scrollWidth
      : 0.5;
    zoomAnchorRef.current = { ...(anchor as { page: number; frac: number }), hRatio };
  }, [scale]);

  // After new page dimensions are applied, force all page containers to the new
  // size synchronously (before paint) and restore the captured anchor position.
  useLayoutEffect(() => {
    if (pageDimensions.size === 0) return;

    // Keep layout deterministic: canvases re-render lazily, so resize their
    // style boxes to the new dimensions now to avoid progressive layout shifts.
    pageDimensions.forEach((dim, pn) => {
      const w = dim.width + 'px';
      const h = dim.height + 'px';
      const canvas = canvasRefs.current.get(pn);
      if (canvas) { canvas.style.width = w; canvas.style.height = h; }
      const cont = containerRefs.current.get(pn);
      if (cont) { cont.style.width = w; cont.style.height = h; }
      const ov = overlayRefs.current.get(pn);
      if (ov) { ov.style.width = w; ov.style.height = h; }
    });

    const anchor = zoomAnchorRef.current;
    if (!anchor) return;
    zoomAnchorRef.current = null;

    const container = document.querySelector('.pdf-viewer-container') as HTMLElement | null;
    const wrapper = pageWrapperRefs.current.get(anchor.page);
    if (!container || !wrapper) return;

    const containerRect = container.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    scrollTrackingRef.current = true;
    const delta = (wrapperRect.top - containerRect.top) + anchor.frac * wrapperRect.height - 1;
    container.scrollTop += delta;
    container.scrollLeft = Math.max(0, anchor.hRatio * container.scrollWidth - containerRect.width / 2);
    window.setTimeout(() => { scrollTrackingRef.current = false; }, 200);
  }, [pageDimensions]);

  // ─────────────────────────────────────────────
  // Scroll-to-box (issue #21): center the requested box in the viewport.
  // Re-runs when pageDimensions settle so it lands correctly after a zoom.
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!scrollToBox || !pdfDoc) return;
    if (completedBoxScrollTokenRef.current === scrollToBox.token) return;

    const box = redactionBoxes[scrollToBox.boxId];
    if (!box) return;

    let cancelled = false;
    (async () => {
      try {
        const page = await pdfDoc.getPage(box.page);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });

        // Wait until the layout reflects the current scale
        const dim = pageDimensions.get(box.page);
        if (!dim || Math.abs(dim.height - viewport.height) > 2) return;

        const container = document.querySelector('.pdf-viewer-container') as HTMLElement | null;
        const wrapper = pageWrapperRefs.current.get(box.page);
        if (!container || !wrapper) return;

        // Rotation-aware box position as a fraction of the page
        const rotation = page.rotate ?? 0;
        const displayScale = viewport.scale;
        const pageWidthPts = viewport.width / displayScale;
        const pageHeightPts = viewport.height / displayScale;
        let [x0, y0, x1, y1] = box.bbox;
        if (rotation === 90) {
          const nx0 = pageWidthPts - y1, ny0 = x0, nx1 = pageWidthPts - y0, ny1 = x1;
          x0 = nx0; y0 = ny0; x1 = nx1; y1 = ny1;
        } else if (rotation === 180) {
          const nx0 = pageWidthPts - x1, ny0 = pageHeightPts - y1, nx1 = pageWidthPts - x0, ny1 = pageHeightPts - y0;
          x0 = nx0; y0 = ny0; x1 = nx1; y1 = ny1;
        } else if (rotation === 270) {
          const nx0 = y0, ny0 = pageHeightPts - x1, nx1 = y1, ny1 = pageHeightPts - x0;
          x0 = nx0; y0 = ny0; x1 = nx1; y1 = ny1;
        }
        const fracX = ((Math.min(x0, x1) + Math.abs(x1 - x0) / 2) / pageWidthPts);
        const fracY = ((Math.min(y0, y1) + Math.abs(y1 - y0) / 2) / pageHeightPts);

        const containerRect = container.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        const targetTop = container.scrollTop
          + (wrapperRect.top - containerRect.top)
          + fracY * viewport.height
          - containerRect.height / 2;
        const targetLeft = container.scrollLeft
          + (wrapperRect.left - containerRect.left)
          + fracX * viewport.width
          - containerRect.width / 2;

        scrollTrackingRef.current = true;
        container.scrollTo({
          top: Math.max(0, targetTop),
          left: Math.max(0, targetLeft),
          behavior: 'smooth',
        });
        window.setTimeout(() => { scrollTrackingRef.current = false; }, 600);
        completedBoxScrollTokenRef.current = scrollToBox.token;
      } catch { /* page not available yet — will retry on next dims update */ }
    })();

    return () => { cancelled = true; };
  }, [scrollToBox, pageDimensions, scale, pdfDoc, redactionBoxes]);

  // ─────────────────────────────────────────────
  // Track current page from scroll position
  // ─────────────────────────────────────────────

  useEffect(() => {
    const container = document.querySelector('.pdf-viewer-container');
    if (!container || !onCurrentPageChange) return;

    const handleScroll = () => {
      if (scrollTrackingRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 3; // Upper third

      let currentPage = 1;
      pageWrapperRefs.current.forEach((el, pageNum) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= centerY && rect.bottom > centerY) {
            currentPage = pageNum;
          }
        }
      });

      if (currentPage !== lastReportedPageRef.current) {
        lastReportedPageRef.current = currentPage;
        onCurrentPageChange(currentPage);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [onCurrentPageChange]);

  // ─────────────────────────────────────────────
  // Drawing functions
  // ─────────────────────────────────────────────

  const startDrawing = (x: number, y: number, pageNum: number) => {
    if (!isDrawMode) return;

    isDrawingRef.current = true;
    startPosRef.current = { x, y };
    drawingPageRef.current = pageNum;

    const overlay = overlayRefs.current.get(pageNum);
    if (!overlay) return;

    const drawingBox = document.createElement('div');
    drawingBox.className = 'redaction-box drawing';
    // Tag the live preview so a re-render mid-drag does not wipe it. Cannot key
    // off the 'drawing' class alone — persisted boxes also get it while drawing.
    drawingBox.dataset.drawingPreview = '1';
    drawingBox.style.left = x + 'px';
    drawingBox.style.top = y + 'px';
    drawingBox.style.width = '0px';
    drawingBox.style.height = '0px';

    overlay.appendChild(drawingBox);
    drawingBoxRef.current = drawingBox;
  };

  const updateDrawing = (x: number, y: number) => {
    if (!isDrawingRef.current || !drawingBoxRef.current) return;

    const startX = startPosRef.current.x;
    const startY = startPosRef.current.y;

    drawingBoxRef.current.style.left = Math.min(startX, x) + 'px';
    drawingBoxRef.current.style.top = Math.min(startY, y) + 'px';
    drawingBoxRef.current.style.width = Math.abs(x - startX) + 'px';
    drawingBoxRef.current.style.height = Math.abs(y - startY) + 'px';
  };

  const finishDrawing = (x: number, y: number) => {
    if (!isDrawingRef.current || !drawingBoxRef.current) return;

    const startX = startPosRef.current.x;
    const startY = startPosRef.current.y;
    const left = Math.min(startX, x);
    const top = Math.min(startY, y);
    const width = Math.abs(x - startX);
    const height = Math.abs(y - startY);

    if (width > 5 && height > 5) {
      const pageNum = drawingPageRef.current;
      const canvasBbox = [left, top, left + width, top + height];
      const pdfBbox = canvasCoordsToPdfCoords(canvasBbox, pageNum);

      const newBox: RedactionBox = {
        bbox: pdfBbox,
        page: pageNum
      };
      onBoxCreate(newBox);
    }

    drawingBoxRef.current.remove();
    drawingBoxRef.current = null;
    isDrawingRef.current = false;
  };

  // ─────────────────────────────────────────────
  // Mouse event handlers
  // ─────────────────────────────────────────────

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const pageNum = getPageFromEvent(e);
    if (!pageNum) return;
    const { x, y } = getRelativeMousePos(e, pageNum);

    // Check resize handle
    const target = e.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {
      if (!isDrawMode) {
        setIsResizing(true);
        setResizeHandle(target.dataset.handle || null);
        const closestBox = target.closest('.redaction-box') as HTMLElement;
        setDragBoxId(closestBox?.dataset.boxId || null);
        setDragStart({ x, y });
      }
      return;
    }

    // Check box click
    const boxElement = target.closest('.redaction-box') as HTMLElement;
    if (boxElement && !boxElement.classList.contains('drawing')) {
      const boxId = boxElement.dataset.boxId;
      if (boxId) {
        if (isDrawMode) return;
        onBoxSelect(boxId, e.ctrlKey || e.metaKey);
        if (!e.ctrlKey && !e.metaKey) {
          setIsDragging(true);
          setDragBoxId(boxId);
          setDragStart({ x, y });
        }
        return;
      }
    }

    // Drawing mode
    if (isDrawMode) {
      startDrawing(x, y, pageNum);
    }
    // Clicking empty canvas deliberately keeps the current selection, so the
    // properties panel stays open. Deselect via the item's checkbox instead.

    onMouseDown(x, y);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    const pageNum = getPageFromEvent(e);
    if (!pageNum) return;
    const { x, y } = getRelativeMousePos(e, pageNum);

    if (isDrawingRef.current) {
      updateDrawing(x, y);
    } else if (isDragging && dragBoxId && !isDrawMode) {
      // Handle box dragging
      const box = redactionBoxes[dragBoxId];
      if (!box) return;
      const overlay = overlayRefs.current.get(box.page);
      const boxElement = overlay?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement && overlay) {
        const overlayW = overlay.offsetWidth;
        const overlayH = overlay.offsetHeight;
        const currentLeft = boxElement.offsetLeft;
        const currentTop = boxElement.offsetTop;

        // Use drag delta relative to the box's page
        const dragPagePos = getRelativeMousePos(e, box.page);
        const deltaX = dragPagePos.x - dragStart.x;
        const deltaY = dragPagePos.y - dragStart.y;

        boxElement.style.left = `${((currentLeft + deltaX) / overlayW) * 100}%`;
        boxElement.style.top = `${((currentTop + deltaY) / overlayH) * 100}%`;
        setDragStart({ x: dragPagePos.x, y: dragPagePos.y });
      }
    } else if (isResizing && dragBoxId && resizeHandle && !isDrawMode) {
      const box = redactionBoxes[dragBoxId];
      if (!box) return;
      const overlay = overlayRefs.current.get(box.page);
      const boxElement = overlay?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement && overlay) {
        const overlayW = overlay.offsetWidth;
        const overlayH = overlay.offsetHeight;

        const resizePagePos = getRelativeMousePos(e, box.page);
        const deltaX = resizePagePos.x - dragStart.x;
        const deltaY = resizePagePos.y - dragStart.y;

        let newLeft = boxElement.offsetLeft;
        let newTop = boxElement.offsetTop;
        let newWidth = boxElement.offsetWidth;
        let newHeight = boxElement.offsetHeight;

        switch (resizeHandle) {
          case 'nw': newLeft += deltaX; newTop += deltaY; newWidth -= deltaX; newHeight -= deltaY; break;
          case 'ne': newTop += deltaY; newWidth += deltaX; newHeight -= deltaY; break;
          case 'sw': newLeft += deltaX; newWidth -= deltaX; newHeight += deltaY; break;
          case 'se': newWidth += deltaX; newHeight += deltaY; break;
          case 'n': newTop += deltaY; newHeight -= deltaY; break;
          case 's': newHeight += deltaY; break;
          case 'w': newLeft += deltaX; newWidth -= deltaX; break;
          case 'e': newWidth += deltaX; break;
        }

        if (newWidth < 10) newWidth = 10;
        if (newHeight < 10) newHeight = 10;

        boxElement.style.left = `${(newLeft / overlayW) * 100}%`;
        boxElement.style.top = `${(newTop / overlayH) * 100}%`;
        boxElement.style.width = `${(newWidth / overlayW) * 100}%`;
        boxElement.style.height = `${(newHeight / overlayH) * 100}%`;
        setDragStart({ x: resizePagePos.x, y: resizePagePos.y });
      }
    }

    onMouseMove(x, y);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    const pageNum = getPageFromEvent(e);
    if (!pageNum) return;
    const { x, y } = getRelativeMousePos(e, pageNum);

    if (isDrawingRef.current) {
      finishDrawing(x, y);
    } else if (isDragging && dragBoxId && !isDrawMode) {
      const box = redactionBoxes[dragBoxId];
      if (box) {
        const overlay = overlayRefs.current.get(box.page);
        const boxElement = overlay?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
        if (boxElement) {
          const left = boxElement.offsetLeft;
          const top = boxElement.offsetTop;
          const width = boxElement.offsetWidth;
          const height = boxElement.offsetHeight;
          const pdfCoords = canvasCoordsToPdfCoords([left, top, left + width, top + height], box.page);
          if (onBoxUpdate) {
            onBoxUpdate(dragBoxId, { ...box, bbox: pdfCoords });
          }
        }
      }
      setIsDragging(false);
      setDragBoxId(null);
    } else if (isResizing && dragBoxId && !isDrawMode) {
      const box = redactionBoxes[dragBoxId];
      if (box) {
        const overlay = overlayRefs.current.get(box.page);
        const boxElement = overlay?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
        if (boxElement) {
          const left = boxElement.offsetLeft;
          const top = boxElement.offsetTop;
          const width = boxElement.offsetWidth;
          const height = boxElement.offsetHeight;
          const pdfCoords = canvasCoordsToPdfCoords([left, top, left + width, top + height], box.page);
          if (onBoxUpdate) {
            onBoxUpdate(dragBoxId, { ...box, bbox: pdfCoords });
          }
        }
      }
      setIsResizing(false);
      setResizeHandle(null);
      setDragBoxId(null);
    }

    onMouseUp(x, y);
  };

  // Canvas clicks intentionally do not clear the selection — see handleMouseDown.
  const handleCanvasClick = (_e: React.MouseEvent) => {
    /* no-op */
  };

  // Safety net for abandoned drags. handleMouseUp returns early when the
  // pointer is released outside any page, so the preview node would linger now
  // that it survives re-renders. React's handler runs first, so on a normal
  // drag drawingBoxRef is already null here and this does nothing.
  useEffect(() => {
    const cancelDanglingDraw = () => {
      if (drawingBoxRef.current) {
        drawingBoxRef.current.remove();
        drawingBoxRef.current = null;
      }
      isDrawingRef.current = false;
    };
    window.addEventListener('mouseup', cancelDanglingDraw);
    return () => window.removeEventListener('mouseup', cancelDanglingDraw);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      renderTasksRef.current.forEach(task => {
        try { task.cancel(); } catch { /* ignore */ }
      });
      renderTasksRef.current.clear();
    };
  }, []);

  // Re-render on DPR change (browser zoom)
  useEffect(() => {
    const mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const handler = () => {
      renderedPagesRef.current.clear();
      renderCacheRef.current.clear();
    };
    mql.addEventListener('change', handler, { once: true });
    return () => mql.removeEventListener('change', handler);
  });

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────

  if (!pdfDoc) {
    return <div className="no-pdf-loaded">{t('noPdfLoaded')}</div>;
  }

  return (
    <div className="pdf-viewer-wrapper">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pn => {
        const dim = pageDimensions.get(pn);
        return (
          <div
            key={pn}
            className="pdf-page-wrapper"
            data-page-num={pn}
            ref={el => { if (el) pageWrapperRefs.current.set(pn, el); }}
            style={{
              width: dim ? `${dim.width}px` : undefined,
              minHeight: dim ? `${dim.height}px` : '800px',
            }}
          >
            <div
              className="pdf-canvas-container"
              ref={el => { if (el) containerRefs.current.set(pn, el); }}
            >
              <canvas
                ref={el => { if (el) canvasRefs.current.set(pn, el); }}
                className="pdf-canvas"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onClick={handleCanvasClick}
              />
              <div
                ref={el => { if (el) overlayRefs.current.set(pn, el); }}
                className="redaction-overlay"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PDFViewer;
